import React, { useState, useEffect } from "react";
import { Container, Row, Col, Form, Card } from "react-bootstrap";
import { IoIosArrowForward } from "react-icons/io";
import { Link, useNavigate, useParams } from "react-router-dom";
import { updatePlan, getPlan } from "../../store/slices/plan";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";

export default function EditPlan() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();

  const { loading } = useSelector((state) => state.plan);

  const [form, setForm] = useState({
    name: "",
    price: "",
    duration: "",
    durationType: "",
    contactViews: "",
    verifiedBadge: false,
    profileHighlight: false,
    description: "",
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!id) return;

    dispatch(getPlan({ id }))
      .unwrap()
      .then((res) => {
        const plan = res?.data || res;

        setForm({
          name: plan.name || "",
          price: String(plan.price || ""),
          duration: String(plan.duration || ""),
          durationType: plan.durationType || "",
          contactViews:
            plan.limits?.contactViews !== undefined
              ? String(plan.limits.contactViews)
              : "",
          verifiedBadge: plan.features?.verifiedBadge || false,
          profileHighlight: plan.features?.profileHighlight || false,
          description: plan.description || "",
        });
      })
      .catch((err) => {
        toast.error(err?.message || "Failed to load plan details.");
      });
  }, [id]);


  const handleChange = (name, value) => {
    const numericFields = ["price", "duration"];

    if (numericFields.includes(name)) {
      value = value.replace(/\D/g, "");
    }

    let error = "";

    switch (name) {
      case "name":
        if (!value.trim()) error = "Please enter plan name";
        break;

      case "price":
        if (!value) error = "Please enter price";
        break;

      case "duration":
        if (!value) error = "Please select duration";
        break;

      case "contactViews":
        if (value === "") error = "Please select contact views";
        break;

      default:
        break;
    }

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: error,
    }));
  };

  const validateForm = () => {
    let newErrors = {};

    if (!form.name)
      newErrors.name = "Please enter plan name";

    if (!form.price)
      newErrors.price = "Please enter price";
    else if (!/^[0-9]+$/.test(form.price))
      newErrors.price = "Price must contain numbers only";

    if (!form.duration)
      newErrors.duration = "Please select duration";
    if (form.contactViews === "") {
      newErrors.contactViews = "Please select contact views";
    }

    return newErrors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const validationErrors = validateForm();
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    // Payload ---- >
    const payload = {
      id,
      name: form.name,
      price: Number(form.price),
      currency: "INR",
      duration: Number(form.duration),
      durationType: form.durationType,

      limits: {
        chats: -1,
        contactViews: Number(form.contactViews),
      },

      features: {
        verifiedBadge: form.verifiedBadge,
        profileHighlight: form.profileHighlight,
      },

      description: form.description,
    };

    dispatch(updatePlan(payload))
      .unwrap()
      .then(() => {
        toast.success("Plan updated successfully!");
        navigate("/admin/plan");
      })
      .catch((err) => {
        const errorMessage =
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          "Failed to update plan.";
        toast.error(errorMessage);
      });
  };

  return (
    <>
      {/* Breadcrumb */}
      <div className="breadcrumb-bar mb-4">
        <div className="breadcrumb-left">
          <Link to="/admin/home" className="breadcrumb-link active">
            Home
          </Link>
          <span className="breadcrumb-separator">
            <IoIosArrowForward size={15} />
          </span>
          <Link to="/admin/plan" className="breadcrumb-link active">
            Plans
          </Link>
          <span className="breadcrumb-separator">
            <IoIosArrowForward size={15} />
          </span>
          <span className="breadcrumb-link inactive">Edit Plan</span>
        </div>
      </div>

      <Container className="py-2">
        <Card className="p-4">
          <Form>
            <Row className="g-3 p-2">

              <Col md={3}>
                <Form.Label className="labelcolor">
                  Plan Name
                </Form.Label>

                <Form.Control
                  type="text"
                  placeholder="Enter Plan Name"

                  value={form.name}
                  maxLength={30}
                  onChange={(e) => handleChange("name", e.target.value)}
                />

                {errors.name && (
                  <small style={{ color: "red", fontSize: "13px" }}>
                    {errors.name}
                  </small>
                )}
              </Col>

              <Col md={3}>
                <Form.Label className="labelcolor">
                  Price (₹)
                </Form.Label>
                <Form.Control

                  value={form.price}
                  maxLength={5}
                  onChange={(e) => handleChange("price", e.target.value)}
                  placeholder="4999"
                />
                {errors.price && (
                  <small style={{ color: "red", fontSize: "13px" }}>
                    {errors.price}
                  </small>
                )}
              </Col>

              <Col md={3}>
                <Form.Label className="labelcolor">
                  Duration
                </Form.Label>
                <Form.Select
                  value={form.duration}
                  onChange={(e) => {
                    const value = e.target.value;

                    handleChange("duration", value);

                    handleChange(
                      "durationType",
                      value === "1" ? "years" : "months"
                    );
                  }}
                >
                  <option value="">Select Duration</option>
                  <option value="3">3 Months</option>
                  <option value="6">6 Months</option>
                  <option value="1">1 Year</option>
                  <option value="2">2 Years</option>
                </Form.Select>
                {errors.duration && (
                  <small style={{ color: "red", fontSize: "13px" }}>
                    {errors.duration}
                  </small>
                )}
              </Col>



              <Col md={3}>
                <Form.Label className="labelcolor">
                  Contact View Limit
                </Form.Label>
                <Form.Select
                  value={form.contactViews}
                  onChange={(e) => handleChange("contactViews", e.target.value)}
                >
                  <option value="">Select Contact Views</option>
                  <option value="-1">Unlimited</option>
                  <option value="25">25</option>
                  <option value="50">50</option>
                  <option value="75">75</option>
                  <option value="100">100</option>
                  <option value="150">150</option>
                  <option value="200">200</option>
                </Form.Select>
                {errors.contactViews && (
                  <small style={{ color: "red", fontSize: "13px" }}>
                    {errors.contactViews}
                  </small>
                )}
              </Col>



              <Col md={3}>
                <Form.Label className="labelcolor">
                  Verify Badge
                </Form.Label>
                <div >
                  <input
                    type="checkbox"
                    checked={form.verifiedBadge === true}
                    onChange={(e) =>
                      handleChange("verifiedBadge", e.target.checked)
                    }

                    className="my-2"
                  />
                  <span style={{ fontSize: "14px", marginLeft: "4px" }}>
                    {form.verifiedBadge ? "Enabled" : "Disabled"}
                  </span>
                </div>
                {errors.verifiedBadge && (
                  <small style={{ color: "red", fontSize: "13px" }}>
                    {errors.verifiedBadge}
                  </small>
                )}
              </Col>
              <Col md={3}>
                <Form.Label>Profile Highlight</Form.Label>

                <div>
                  <input
                    type="checkbox"
                    checked={form.profileHighlight}
                    onChange={(e) => handleChange("profileHighlight", e.target.checked)}
                  />

                  <span style={{ marginLeft: 5 }}>
                    {form.profileHighlight ? "Enabled" : "Disabled"}
                  </span>
                </div>

              </Col>


              <Col md={12}>
                <Form.Label className="labelcolor">
                  Description
                </Form.Label>
                <Form.Control
                  as="textarea"
                  value={form.description}
                  onChange={(e) => handleChange("description", e.target.value)}
                  placeholder="Enter Description"
                />
              </Col>
            </Row>

            <div className="d-flex justify-content-center gap-3 mt-4">
              <button
                type="button"
                className="cancel-btn"
                onClick={() => navigate("/admin/plan")}
              >
                Cancel
              </button>
              <button
                type="button"
                className="submit-btn"
                onClick={handleSubmit}
                disabled={loading}
              >
                {loading ? "Submitting..." : "Submit"}
              </button>
            </div>
          </Form>
        </Card>
      </Container >
    </>
  );
}
